import { Controller } from "@hotwired/stimulus";

const TOOL_CARD_SELECTOR = '[data-testid="ticket-tool-call-card"]';
const TOOL_CARD_OPEN_STORAGE_PREFIX = "ticketChatToolCardOpen:";

export default class extends Controller<HTMLElement> {
  static values = { intervalMs: { type: Number, default: 5000 } };
  declare readonly intervalMsValue: number;

  private refreshTimer: number | null = null;
  private refreshRequestInFlight = false;
  private gracePollsRemaining = 0;
  private lastActiveRunId = "";
  private lastCompletedRunId = "";

  private readonly handleBeforeSwap = (event: Event): void => {
    if (this.updatesChat(event)) this.captureToolCardStates(this.chatFragment());
  };
  private readonly handleAfterSwap = (event: Event): void => {
    if (!this.updatesChat(event)) return;
    this.restoreToolCardStates(this.chatFragment());
    this.observeRun();
    this.scheduleRefresh();
  };
  private readonly handleBeforeRequest = (event: Event): void => {
    if (this.updatesChat(event) || event.target === this.element) {
      this.refreshRequestInFlight = true;
    }
  };
  private readonly handleAfterRequest = (event: Event): void => {
    if (this.updatesChat(event) || event.target === this.element) {
      this.refreshRequestInFlight = false;
      this.scheduleRefresh();
    }
  };
  private readonly handleStreamDone = (event: Event): void => {
    const fragment = this.chatFragment();
    const runId = (event as CustomEvent).detail?.snapshot?.runId;
    if (event.target instanceof Node && fragment?.contains(event.target) &&
        runId && runId === this.lastActiveRunId) {
      this.refreshDetailAfterRun(runId);
    }
  };
  private readonly handleToolCardToggle = (event: Event): void => {
    const target = event.target;
    if (target instanceof HTMLDetailsElement && target.matches(TOOL_CARD_SELECTOR)) {
      this.storeToolCardState(target);
    }
  };

  connect(): void {
    document.body.addEventListener("htmx:beforeSwap", this.handleBeforeSwap);
    document.body.addEventListener("htmx:afterSwap", this.handleAfterSwap);
    document.body.addEventListener("htmx:beforeRequest", this.handleBeforeRequest);
    document.body.addEventListener("htmx:afterRequest", this.handleAfterRequest);
    document.body.addEventListener("ticket-chat-stream:done", this.handleStreamDone);
    document.addEventListener("ihp:auto-refresh-update", this.handleAfterSwap);
    document.addEventListener("toggle", this.handleToolCardToggle, true);
    this.restoreToolCardStates(this.chatFragment());
    this.observeRun();
    this.scheduleRefresh();
  }

  disconnect(): void {
    document.body.removeEventListener("htmx:beforeSwap", this.handleBeforeSwap);
    document.body.removeEventListener("htmx:afterSwap", this.handleAfterSwap);
    document.body.removeEventListener("htmx:beforeRequest", this.handleBeforeRequest);
    document.body.removeEventListener("htmx:afterRequest", this.handleAfterRequest);
    document.body.removeEventListener("ticket-chat-stream:done", this.handleStreamDone);
    document.removeEventListener("ihp:auto-refresh-update", this.handleAfterSwap);
    document.removeEventListener("toggle", this.handleToolCardToggle, true);
    if (this.refreshTimer !== null) window.clearTimeout(this.refreshTimer);
  }

  private chatFragment(): HTMLElement | null {
    return document.getElementById("tickets-chat-fragment");
  }

  private updatesChat(event: Event): boolean {
    const target = (event as CustomEvent).detail?.target;
    const fragment = this.chatFragment();
    return target instanceof HTMLElement &&
      (target.id === "tickets-chat-fragment" || Boolean(fragment && target.contains(fragment)));
  }

  private observeRun(): void {
    const activeRunId = this.chatFragment()?.dataset.activeRunId || "";
    // The HTML transition also covers completion when the SSE connection is lost.
    if (this.lastActiveRunId && activeRunId !== this.lastActiveRunId) {
      this.refreshDetailAfterRun(this.lastActiveRunId);
    }
    this.lastActiveRunId = activeRunId;
  }

  private refreshDetailAfterRun(runId: string): void {
    if (runId === this.lastCompletedRunId) return;
    this.lastCompletedRunId = runId;
    document.body.dispatchEvent(new CustomEvent("ticket-detail-refresh", { bubbles: true }));
  }

  private scheduleRefresh(): void {
    if (this.refreshTimer !== null) window.clearTimeout(this.refreshTimer);
    this.refreshTimer = null;
    const fragment = this.chatFragment();
    if (!fragment || (!fragment.dataset.activeRunId &&
        fragment.dataset.testid !== "tickets-chat-fragment-loader" && !this.gracePollsRemaining)) return;

    // Any applied HTTP or WebSocket update restarts this single inactivity timer.
    // A connected socket alone does not prove that the screen is up to date.
    this.refreshTimer = window.setTimeout(() => {
      this.refreshTimer = null;
      this.observeRun();
      const current = this.chatFragment();
      if (!current) return;
      if (!this.refreshRequestInFlight) {
        if (current.dataset.activeRunId) this.gracePollsRemaining = 3;
        else if (this.gracePollsRemaining) this.gracePollsRemaining -= 1;
        else if (current.dataset.testid !== "tickets-chat-fragment-loader") return;
        this.element.dispatchEvent(new CustomEvent("ticket-chat-refresh", { bubbles: true }));
      }
      this.scheduleRefresh();
    }, Math.max(250, this.intervalMsValue));
  }

  private captureToolCardStates(root: HTMLElement | null): void {
    if (!root) return;

    root.querySelectorAll<HTMLDetailsElement>(TOOL_CARD_SELECTOR).forEach((card) => {
      this.storeToolCardState(card);
    });
  }

  private restoreToolCardStates(root: HTMLElement | null): void {
    if (!root) return;

    root.querySelectorAll<HTMLDetailsElement>(TOOL_CARD_SELECTOR).forEach((card) => {
      const state = this.readToolCardState(card);
      if (state === null) return;

      card.open = state;
    });
  }

  private storeToolCardState(card: HTMLDetailsElement): void {
    const key = this.toolCardStorageKey(card);
    if (!key) return;

    try {
      window.sessionStorage.setItem(key, card.open ? "1" : "0");
    } catch {
      // Storage can be disabled in private or constrained browser contexts.
    }
  }

  private readToolCardState(card: HTMLDetailsElement): boolean | null {
    const key = this.toolCardStorageKey(card);
    if (!key) return null;

    try {
      const value = window.sessionStorage.getItem(key);
      if (value === "1") return true;
      if (value === "0") return false;
      return null;
    } catch {
      return null;
    }
  }

  private toolCardStorageKey(card: HTMLDetailsElement): string | null {
    const message = card.closest<HTMLElement>("[data-message-id]");
    const messageId = message?.dataset.messageId;
    if (!message || !messageId) return null;

    const cards = Array.from(message.querySelectorAll<HTMLDetailsElement>(TOOL_CARD_SELECTOR));
    const cardIndex = cards.indexOf(card);
    if (cardIndex < 0) return null;

    const name = card.dataset.toolName || "";
    const provider = card.dataset.toolProvider || "";
    return `${TOOL_CARD_OPEN_STORAGE_PREFIX}${messageId}:${cardIndex}:${provider}:${name}`;
  }

}
